import { useCallback, useEffect, useState } from "react";
import { getDataClient } from "@/lib/dataClient";
import { apiRequest } from "@/lib/api";

export interface StudentGoal {
  id: string;
  targetNet: number | null;
  targetSchool: string;
  targetExamDate: string;
}

const EMPTY: StudentGoal = {
  id: "",
  targetNet: null,
  targetSchool: "",
  targetExamDate: "",
};

export function useGoals() {
  const [goal, setGoal] = useState<StudentGoal>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const dataClient = getDataClient();
      const { data: authData } = await dataClient.auth.getUser();
      if (!authData.user) {
        setGoal(EMPTY);
        return;
      }

      // Hedefler öğrencinin hesabına bağlı özel kayıtlardır. Genel uygulama
      // kayıt deposu yerine bu amaçla hazırlanmış yetkili API kullanılır.
      const { goal: row } = await apiRequest<{
        goal: { id: string; target_net: number | null; target_school: string | null; target_exam_date: string | null } | null;
      }>("/student/goals");
      if (row) {
        setGoal({
          id: row.id,
          targetNet: row.target_net != null ? Number(row.target_net) : null,
          targetSchool: row.target_school ?? "",
          targetExamDate: row.target_exam_date ?? "",
        });
      } else {
        setGoal(EMPTY);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Hedef yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (input: { targetNet: number | null; targetSchool: string; targetExamDate: string }) => {
      const dataClient = getDataClient();
      const { data: authData } = await dataClient.auth.getUser();
      if (!authData.user) return { ok: false, message: "Hedef kaydetmek için giriş yapmalısın." };

      const payload = {
        target_net: input.targetNet,
        target_school: input.targetSchool || null,
        target_exam_date: input.targetExamDate || null,
      };

      try {
        await apiRequest("/student/goals", { method: "PUT", body: payload });
      } catch (error) {
        return {
          ok: false,
          message: error instanceof Error ? error.message : "Hedef kaydedilemedi.",
        };
      }
      await load();
      return { ok: true, message: "Hedefin kaydedildi." };
    },
    [load],
  );

  return { goal, loading, error, reload: load, save };
}
